import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, PaywallNotice, GenerateButton } from "@/components/shared";
import { useAutosave } from "@/hooks/useAutosave";
import RemainingCounter from "@/components/RemainingCounter";
import PremiumHint from "@/components/PremiumHint";
import { toast } from "sonner";
import { Instagram, Music2, Facebook, AtSign, Youtube, Sparkles, Copy } from "lucide-react";

const PLATFORMS = [
  { id: "Instagram", icon: Instagram },
  { id: "TikTok", icon: Music2 },
  { id: "Facebook", icon: Facebook },
  { id: "Threads", icon: AtSign },
  { id: "YouTube", icon: Youtube },
];

const FIELDS = [
  { key: "title", label: "Titre" },
  { key: "hook", label: "Hook" },
  { key: "rehook", label: "Re-hook" },
  { key: "script", label: "Script complet" },
  { key: "conclusion", label: "Conclusion" },
  { key: "description", label: "Description optimisée" },
];

export default function Create() {
  const [params] = useSearchParams();
  const [platform, setPlatform] = useState("Instagram");
  const [topic, setTopic] = useState("");
  useAutosave("create", topic, setTopic);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [usageTrigger, setUsageTrigger] = useState(0);

  const generate = async (over) => {
    const t = (typeof over === "string" ? over : topic).trim();
    if (!t) return;
    setBusy(true); setPaywall(false); setResult(null);
    try {
      const { data } = await api.post("/ai/content", { platform: (params.get("platform") || platform), topic: t });
      setResult(data);
      setUsageTrigger((t2) => t2 + 1);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const pl = params.get("platform");
    const tp = params.get("topic");
    if (pl && PLATFORMS.some((p) => p.id === pl)) setPlatform(pl);
    if (tp) setTopic(tp);
    if (tp && params.get("auto") === "1") generate(tp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <Container>
      <PageHeader eyebrow="Création" title="Créateur de contenu"
        subtitle="Choisissez une plateforme, indiquez votre sujet, obtenez un script complet et engageant." />

      <div className="mb-4"><RemainingCounter kind="content" trigger={usageTrigger} /></div>

      <div className="card-surface p-5 mb-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const active = platform === p.id;
            return (
              <button key={p.id} data-testid={`platform-${p.id}`} onClick={() => setPlatform(p.id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${
                  active ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-white/60 hover:border-white/20"
                }`}>
                <Icon size={16} strokeWidth={1.5} /> {p.id}
              </button>
            );
          })}
        </div>
        <input data-testid="content-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)}
          placeholder="Votre sujet (ex : les mythes sur la productivité)"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 outline-none text-white placeholder:text-white/30" />
        <div className="flex items-center justify-between gap-3 mt-3">
          <MicButton testid="content-topic-mic" value={topic} onChange={setTopic} />
          <GenerateButton data-testid="content-generate-button" onClick={generate} disabled={busy || !topic.trim()}>
            <Sparkles size={16} /> Générer le contenu
          </GenerateButton>
        </div>
      </div>

      {busy && <Spinner />}
      {paywall && <PaywallNotice />}

      {result && (
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key} data-testid={`content-field-${f.key}`} className="card-surface p-5 fade-up">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gold text-xs uppercase tracking-[0.15em]">{f.label}</span>
                <button onClick={() => { navigator.clipboard.writeText(result[f.key] || ""); toast.success("Copié !"); }}
                  className="text-white/40 hover:text-gold"><Copy size={15} /></button>
              </div>
              <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{result[f.key]}</p>
            </div>
          ))}
        </div>
      )}

      <PremiumHint text="Passez Premium pour la création de contenu avancée et des scripts en illimité (Pro)." />
    </Container>
  );
}
